import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useAppSelector } from "../../Store";
import { Graph_QL } from "../../Services";
import { useSection } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useAnimal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { colors, fonts, frames, glyphs, icons, Units, useUnitsComponent } from "../../Styles";
import { Button, ButtonList, NewSelect, NewSelectList, PageHeader, ProgressBar } from "../../Components";
import { ButtonTypes, ListStyle_Types, NewFieldStyles } from "../../Definitions";
import { useFeedCurves } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";

// TODO
import FeedCorrectionPopover from "../../Components/LocationFeeder/feedCorrectionPopover";
import FeedSkipPopover from "../../Components/LocationFeeder/feedSkipPopover";
import PenModal from "../../Components/OperationsComponents/Modals/penModal";
import MoveAnimalModal from "../../Components/OperationsComponents/Modals/moveAnimalModal";
import { Pages } from "../../Navigation/Routers/root";
import { LfLocation } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { useSelectOptions } from "../../Components/_Components";

const SYSTEM_ID = 1;

export default function OP_Operations() {
  const { t } = useTranslation();
  const getUnit = useUnitsComponent();
  const [locationToggle, setLocationToggle] = useState<boolean>(false);
  const [selectedPen, setSelectedPen] = useState<LfLocation>();
  const [selectedSection, setSelectedSection] = useState<number | undefined>(undefined);
  const [animalDetailsModal, setAnimalDetailsModal] = useState<boolean>(false);
  const [moveAnimalModal, setMoveAnimalModal] = useState<boolean>(false);

  const {systemOptions, sectionOptions, locationOptions} = useSelectOptions()

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : SYSTEM_ID;
  const animal = selectedPen?.animals && selectedPen.animals.length ? selectedPen.animals[0] : undefined;

  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection);
  const { call: getFeedCurves, data: feedCurvesState } = useFeedCurves(connection);

  const sections = useMemo(
    () => (lfSectionsState[domain] ? lfSectionsState[domain].data : undefined),
    [domain, lfSectionsState]
  );
  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    getFeedCurves({ fetchAmount: 50, ifFeedCurveBaseInfoOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pen Options
  const pensOptions = useMemo(() => {
    if (!sections || !selectedSection) return [];

    return (
      sections.find((section) => section.section_id === selectedSection)?.section_locations ?? []);
  }, [sections, selectedSection]);

  // Set selected pen to first in list on page load
  useEffect(() => {
    if (sections && sections.length > 0 && !selectedSection) {
      setSelectedSection(sections[0].section_id);
    }
  }, [sections, selectedSection]);

  // Set selected pen to first in list on section change
  useEffect(() => {
    if (pensOptions.length > 0) {
      setSelectedPen(pensOptions[0]);
    }
  }, [pensOptions]);

  // FeedCurve Options
  const feedCurves = useMemo(() => {
    if (feedCurvesState && feedCurvesState[domain]) {
      return feedCurvesState[domain].data;
    }
    return [];
  }, [domain, feedCurvesState]);

  // Progress Bar
  // TODO: Use correct data from feedcurve/animal
  // const animalFoodTotal = selectedPen?.animals[0].consumptions?.[0].total
  //   ? selectedPen.animals.length
  //     ? selectedPen.animals[0].consumptions?.[0].total
  //     : 0
  //   : 0;
  // const animalFoodProgressed = selectedPen?.animals[0].consumptions?.[1].total
  //   ? selectedPen.animals.length
  //     ? selectedPen.animals[0].consumptions?.[1].total
  //     : 0
  //   : 0;

  // Seperator
  const seperator = (
    <div
      style={{width: "80%", minWidth: "280px", height: "2px", backgroundColor: colors.greyTertiary}}/>);

  // #region | Parity Buttons
  const currentIndex = pensOptions.findIndex((pen) => pen.location_id === selectedPen?.location_id);

  const previousPenBtn = (
    <Button
      content={t("buttons.prev", { defaultValue: "prev" })}
      buttonType={ButtonTypes.textButton}
      startEnhancer={glyphs.Arrow({ rotate: 180 })}
      onClick={() => {setSelectedPen(pensOptions[(currentIndex - 1 + pensOptions.length) % pensOptions.length])}}
    />
  );

  const nextPenBtn = (
    <Button
      content={t("buttons.next", { defaultValue: "next" })}
      buttonType={ButtonTypes.textButton}
      endEnhancer={glyphs.Arrow({})}
      onClick={() => {setSelectedPen(pensOptions[(currentIndex + 1) % pensOptions.length])}}
    />
  );
  // #endregion

  return (
    <frames.Page_inner_operations>
      <frames._page_inner_header>
        <PageHeader topHeader={t(`menuHeaders.${Pages.op_operations}`, {defaultValue: "operations"})}/>
      </frames._page_inner_header>
      <frames._page_inner_content>
        <div style={{display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: locationToggle ? "160px 1fr" : "35px 1fr", height: "100%", width: "100%", gap: "20px"}}>
          {/* Location */}
          <div
            style={{gridArea: "1/1/2/2", backgroundColor: colors.greyDarkPrimary, borderRadius: "10px", padding: "10px 20px", boxSizing: "border-box"}}>
            <div
              style={{ display: "flex", gap: "10px", cursor: "pointer" }}
              onClick={() => setLocationToggle(!locationToggle)}
            >
              <p style={{...fonts.header,...fonts.fontSize4,color: colors.greyLightTertiary,marginBottom: "0px"}}>
                {t(`operationsPages.location`, { defaultValue: "location" })}
              </p>
              <glyphs.Arrow transparency={locationToggle ? 1 : 0.3} rotate={locationToggle ? 90 : 0} size="15px" />
            </div>
            {locationToggle ? (
              <div style={{ marginTop: "20px" }}>
                <NewSelectList
                  options={[
                    systemOptions,
                    sectionOptions ?? []
                  ]}
                  onChanges={[
                    (i) => {},
                    (i) => { setSelectedSection(Number(i?.id)) },
                  ]}
                  placeholders={["", ""]}
                  headers={[ t(`operationsPages.system`, { defaultValue: "system" }), t(`operationsPages.section`, { defaultValue: "section" }) ]}
                  fieldOverrides={{
                    fieldStyle: NewFieldStyles.greyDarkSecondary,
                    fieldWidth: "100%",
                  }}
                  listOverrides={{
                    listStyle: ListStyle_Types.vertical,
                    listGap: "10px",
                  }}
                  valueOverrides={{
                    defaultValues: [
                      systemOptions?.[0] || { label: "", id: 0 },
                      sectionOptions?.[0] || { label: "", id: 0 },
                    ],
                  }}
                />
              </div>
            ) : (
              <></>
            )}
          </div>

          {/* Animal */}
          <div
            style={{
              gridArea: "2/1/3/2",
              backgroundColor: colors.greyLightTertiary,
              border: "5px solid " + colors.greyPrimary,
              borderRadius: "10px",
              boxSizing: "border-box",
              display: "grid",
              gridTemplateColumns: "1fr",
              gridTemplateRows: "35px 1fr 50px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                gridArea: "1/1/2/2",
                backgroundColor: colors.greyPrimary,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                padding: "2px 0px 7px 0px",
              }}
            >
              {previousPenBtn}
              <NewSelect
                key={selectedPen?.location_id}
                options={pensOptions.map((pen) => ({
                  id: pen.location_id,
                  label: pen.name,
                }))}
                onChange={(i) => {
                  setSelectedPen(
                    pensOptions.find((pen) => pen.location_id === i?.id)
                  );
                }}
                fieldOverrides={{
                  fieldWidth: "100px",
                  fieldStyle: NewFieldStyles.greySecondary,
                }}
                valueOverrides={{
                  defaultValue: pensOptions.length
                    ? selectedPen
                      ? {
                          id: selectedPen?.location_id,
                          label: selectedPen?.name,
                        }
                      : {
                          id: pensOptions[0].location_id,
                          label: pensOptions[0].name,
                        }
                    : undefined,
                }}
              />
              {nextPenBtn}
            </div>

            <div
              style={{
                gridArea: "2/1/4/2",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "start",
                gap: "20px",
                padding: "20px 0px 70px 0px",
                boxSizing: "border-box",
                overflowY: "scroll",
              }}
            >
              {/* Animal from Location */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                }}
              >
                <img
                  src={icons.animalsColor}
                  style={{ width: "40px" }}
                  alt="icon"
                />
                <div>
                  <p
                    style={{
                      ...fonts.header,
                      ...fonts.fontSize4,
                      color: colors.greyPrimary,
                    }}
                  >
                    {t(`operationsPages.inThisPen`, {
                      defaultValue: "in this pen",
                    })}
                  </p>
                  <p
                    style={{
                      ...fonts.header,
                      ...fonts.fontSize3,
                      color: colors.greyDarkPrimary,
                    }}
                  >{`${t("animalPages.animal.animal", {
                    defaultValue: "animal",
                  })} ${animal?.animal_id}`}</p>
                </div>
              </div>
              {seperator}
              {/* Activations and ProgressBar */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "20px",
                }}
              >
                {/* <Activations pen={selectedPen} showActivations /> */}
                {/* <ProgressBar
                  animation
                  header={`${t("feed.general.remainingToday", {
                    defaultValue: "remaining today",
                  })}`}
                  total={animalFoodTotal ?? 0}
                  progressed={animalFoodProgressed ?? 0}
                  unit={getUnit(Units.kG_grey)}
                /> */}
              </div>
              {seperator}
              <div
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",gap: "10px"}}>
                {/* FeedCurve */}
                <NewSelect
                  header={`${t("feed.feedCurve.feedCurve", {
                    defaultValue: "feed curve",
                  })}`}
                  options={feedCurves.map((feedCurve) => ({
                    label: feedCurve.name,
                    id: feedCurve.feedCurveId,
                  }))}
                  fieldOverrides={{
                    fieldStyle: NewFieldStyles.greyTertiary,
                  }}
                  valueOverrides={{
                    defaultValue: animal?.feedCurve?.name
                      ? {
                          label: animal.feedCurve.name,
                          id: animal.feedCurve.feedCurveId,
                        }
                      : undefined,
                  }}
                />
                {/* FeedCorrection */}
                <FeedCorrectionPopover
                  header={`${t("feed.feedCorrection.feedCorrection", {
                    defaultValue: "feed correction",
                  })}`}
                  fieldOverrides={{ fieldHeight: "30px" }}
                  operationView
                />
                {/* FeedSkip */}
                <FeedSkipPopover
                  pen={selectedPen}
                  header={`${t("feed.feedSkip.feedSkip", {
                    defaultValue: "feed skip",
                  })}`}
                  fieldOverrides={{ fieldHeight: "30px" }}
                  operationView
                />
              </div>
            </div>
            {/* Details, Move Btns */}
            <div
              style={{
                gridArea: "3/1/4/2",
                display: "flex",
                justifyContent: "end",
                padding: "0px 20px",
              }}
            >
              <ButtonList
                contents={[
                  `${t("buttons.details", { defaultValue: "details" })}`,
                  `${t("buttons.move", { defaultValue: "move" })}`,
                ]}
                onClick={(i) => {
                  if (i === 0) {
                    setAnimalDetailsModal(true);
                  }
                  if (i === 1) {
                    setMoveAnimalModal(true);
                  }
                }}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[glyphs.Animal({}), glyphs.AnimalMove({})]}
              />
            </div>
          </div>
          <PenModal
            isModalOpen={!!animalDetailsModal}
            onClose={() => setAnimalDetailsModal(false)}
            pen={selectedPen}
            sectionId={selectedSection ?? 0}
            systemId={SYSTEM_ID}
          />
          <MoveAnimalModal
            isModalOpen={moveAnimalModal}
            onClose={() => setMoveAnimalModal(false)}
            pen={selectedPen}
          />
        </div>
      </frames._page_inner_content>
    </frames.Page_inner_operations>
  );
}
